/**
 * Luxe Treasure Jewelry — Review Generator
 *
 * This script powers the review generator widget.
 * All code is wrapped in an IIFE to avoid polluting the global scope
 * and to prevent conflicts with Shopify theme JavaScript.
 */
(function () {
  "use strict";

  // ============================================================
  // CONFIGURATION — Update these values for your deployment
  // ============================================================

  /**
   * Replace "YOUR-PROJECT" with your actual Cloudflare Pages project name.
   * Example: "https://luxe-review-shopify-page.pages.dev/api/generate"
   */
  const REVIEW_API_URL = "https://luxe-review-shopify-page.pages.dev/api/generate";

  /**
   * Your Google Business review link.
   * Replace the placeid value with your actual Google Place ID.
   * Find yours at: https://developers.google.com/maps/documentation/places/web-service/place-id
   */
  const GOOGLE_REVIEW_URL =
    "https://search.google.com/local/writereview?placeid=ChIJM5HZPJxFwokRZXOmHqj0918";

  // ============================================================
  // STATE
  // ============================================================
  let selectedRating = null;
  let selectedKeywords = [];

  // ============================================================
  // DOM REFERENCES
  // ============================================================
  const container = document.getElementById("luxe-review-generator");
  if (!container) {
    console.warn("Review generator: #luxe-review-generator not found in DOM.");
    return;
  }

  const ratingButtons = container.querySelectorAll(".lrg-rating-btn");
  const keywordTags = container.querySelectorAll(".lrg-keyword-tag");
  const generateBtn = container.querySelector("#lrg-generate-btn");
  const errorDiv = container.querySelector("#lrg-error");
  const outputDiv = container.querySelector("#lrg-output");
  const reviewTextarea = container.querySelector("#lrg-review-text");
  const postBtn = container.querySelector("#lrg-post-btn");
  const successMsg = container.querySelector("#lrg-success-msg");

  // ============================================================
  // EVENT LISTENERS
  // ============================================================

  // --- Rating buttons: select one at a time ---
  ratingButtons.forEach(function (btn) {
    btn.addEventListener("click", function () {
      // Deselect all
      ratingButtons.forEach(function (b) {
        b.classList.remove("active");
      });
      // Select clicked
      btn.classList.add("active");
      selectedRating = btn.getAttribute("data-rating");
      updateGenerateButton();
    });
  });

  // --- Keyword tags: toggle selection ---
  keywordTags.forEach(function (tag) {
    tag.addEventListener("click", function () {
      var keyword = tag.getAttribute("data-keyword");
      tag.classList.toggle("active");

      if (tag.classList.contains("active")) {
        // Add keyword if not already present
        if (selectedKeywords.indexOf(keyword) === -1) {
          selectedKeywords.push(keyword);
        }
      } else {
        // Remove keyword
        selectedKeywords = selectedKeywords.filter(function (k) {
          return k !== keyword;
        });
      }
    });
  });

  // --- Generate button ---
  generateBtn.addEventListener("click", function () {
    generateReview();
  });

  // --- Copy to clipboard, then open Google review page ---
  postBtn.addEventListener("click", function () {
    copyAndPost();
  });

  // ============================================================
  // FUNCTIONS
  // ============================================================

  /**
   * Enable/disable the generate button based on whether a rating is selected.
   */
  function updateGenerateButton() {
    generateBtn.disabled = !selectedRating;
  }

  /**
   * Show an error message to the user.
   */
  function showError(message) {
    errorDiv.textContent = message;
    errorDiv.style.display = "block";
  }

  /**
   * Hide the error message.
   */
  function hideError() {
    errorDiv.style.display = "none";
    errorDiv.textContent = "";
  }

  /**
   * Set the generate button to a loading state.
   */
  function setLoading(isLoading) {
    if (isLoading) {
      generateBtn.textContent = "Generating...";
      generateBtn.classList.add("loading");
      generateBtn.disabled = true;
    } else {
      generateBtn.textContent = "Generate My Review";
      generateBtn.classList.remove("loading");
      updateGenerateButton();
    }
  }

  /**
   * Main function: call the API and display the generated review.
   */
  function generateReview() {
    // Validate
    if (!selectedRating) {
      showError("Please select a rating first.");
      return;
    }

    if (selectedKeywords.length === 0) {
      showError("Please select at least one keyword.");
      return;
    }

    hideError();
    setLoading(true);
    outputDiv.style.display = "none";
    successMsg.style.display = "none";

    // Call the Cloudflare Pages Function
    fetch(REVIEW_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        rating: selectedRating,
        keywords: selectedKeywords,
      }),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        setLoading(false);

        if (data.success && data.review) {
          // Show the output section with the generated review
          reviewTextarea.value = data.review;
          outputDiv.style.display = "block";

          // Scroll the output into view
          outputDiv.scrollIntoView({ behavior: "smooth", block: "nearest" });
        } else {
          showError(data.error || "Failed to generate review. Please try again.");
        }
      })
      .catch(function (err) {
        setLoading(false);
        console.error("Review generator fetch error:", err);
        showError(
          "Unable to connect to the review service. Please check your internet connection and try again."
        );
      });
  }

  /**
   * Copy the review to clipboard, then open the Google review page.
   */
  function copyAndPost() {
    var text = reviewTextarea.value;

    if (!text) {
      return;
    }

    // Modern clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(function () {
          showCopySuccess();
          openGoogle();
        })
        .catch(function () {
          fallbackCopy(text);
          openGoogle();
        });
    } else {
      // Fallback for older browsers
      fallbackCopy(text);
      openGoogle();
    }
  }

  /**
   * Fallback copy method using a temporary textarea element.
   */
  function fallbackCopy(text) {
    var tempTextarea = document.createElement("textarea");
    tempTextarea.value = text;
    tempTextarea.style.position = "fixed";
    tempTextarea.style.left = "-9999px";
    tempTextarea.style.top = "-9999px";
    document.body.appendChild(tempTextarea);
    tempTextarea.select();

    try {
      document.execCommand("copy");
      showCopySuccess();
    } catch (err) {
      showError("Unable to copy. Please select the text and copy manually.");
    }

    document.body.removeChild(tempTextarea);
  }

  /**
   * Open the Google review page in a new tab.
   */
  function openGoogle() {
    window.open(GOOGLE_REVIEW_URL, "_blank");
  }

  /**
   * Show a brief "Copied!" confirmation message.
   */
  function showCopySuccess() {
    successMsg.textContent = "Review copied! Paste it on Google.";
    successMsg.style.display = "block";

    // Hide after 4 seconds
    setTimeout(function () {
      successMsg.style.display = "none";
    }, 4000);
  }
})();